import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  Stores,
  Users,
  ValidateAliasAvailable,
  initialStoreUserExperience,
  Customers,
  CustomEndpoint,
  CustomerDashboardState,
  EventState,
} from './stores.reducer';
import { ClientStore, StoreOrderItemsStatistics } from '../stores';

export const getStoresState = createFeatureSelector<Stores>('stores');
export const getStoresList = createSelector(
  getStoresState,
  (state: Stores) => state.list,
);
export const getSelectedStore = createSelector(
  getStoresState,
  (state: Stores) => state?.selectedStore.store,
);
export const getSelectedStoreApiStatus = createSelector(
  getStoresState,
  (state: Stores) => state.selectedStore.status,
);
export const getSelectedStoreLocale = createSelector(
  getSelectedStore,
  (state: ClientStore) => state.locale,
);
export const getSelectedStoreTimezone = createSelector(
  getSelectedStore,
  (state: ClientStore) => state.timeZone,
);

export const getUsersState = createFeatureSelector<Users>('users');
export const getUsersList = createSelector(
  getUsersState,
  (state: Users) => state.userList,
);

export const getInviteUser = createSelector(
  getUsersState,
  (state: Users) => state.inviteUser,
);

export const getStoreImage = createSelector(
  getStoresState,
  (state: Stores) => state.selectedStoreImages.imageUrl,
);
export const getStoreLogo = createSelector(
  getStoresState,
  (state: Stores) => state.selectedStoreImages.logoUrl,
);
export const getStoreBackgroundImage = createSelector(
  getStoresState,
  (state: Stores) => state.selectedStoreImages.backgroundImageUrl,
);
export const getStoreStatistics = createSelector(
  getStoresState,
  (state: Stores) => state.statisticsList.statistics,
);
export const getMenuViewingStatistics = createSelector(
  getStoresState,
  (state: Stores) => state.statisticsList.menuViewingStatistics,
);
export const getLoyaltyStatistics = createSelector(
  getStoresState,
  (state: Stores) => state.statisticsList.loyaltyStatistics,
);
export const getLoyaltyStatisticsStatus = createSelector(
  getStoresState,
  (state: Stores) => state.statisticsList.loyaltyStatisticsStatus,
);
export const getLoyaltyStatisticsStore = createSelector(
  getStoresState,
  (state: Stores) => state.statisticsList.loyaltyStatisticsStore,
);
export const getLoyaltyStatisticsPeriod = createSelector(
  getStoresState,
  (state: Stores) => state.statisticsList.loyaltyStatisticsPeriod,
);

export const getAliasAvailability =
  createFeatureSelector<ValidateAliasAvailable>('validateAliasAvailable');
export const getAliasAvailabilityState = createSelector(
  getAliasAvailability,
  (state: ValidateAliasAvailable) =>
    state.aliasAvailabilityState.availabilityState,
);
export const getAliasAvailabilityStatus = createSelector(
  getAliasAvailability,
  (state: ValidateAliasAvailable) => state.aliasAvailabilityState.status,
);

export const getStoreNotificationsEnabled = createSelector(
  getStoresState,
  (state: Stores) => state.selectedStore.notificationsEnabled,
);
export const getStoreNotificationSupported = createSelector(
  getStoresState,
  (state: Stores) => state.selectedStore.notificationsSupported,
);

export const getStoreAutoprintEnabled = createSelector(
  getStoresState,
  (state: Stores) => state.selectedStore.autoprintEnabled,
);
export const getStoreAutoprintSupported = createSelector(
  getStoresState,
  (state: Stores) => state.selectedStore.autoprintSupported,
);

export const getStoreZones = createSelector(
  getStoresState,
  (state: Stores) => state.zonesList.zones,
);
export const getStoreZoneStatus = createSelector(
  getStoresState,
  (state: Stores) => state.zoneState.status,
);
export const getStoreZone = createSelector(
  getStoresState,
  (state: Stores) => state.zone.zone,
);
export const getStoreSingleZoneStatus = createSelector(
  getStoresState,
  (state: Stores) => state.zone.zoneStatus,
);

export const getLoadingStoreId = createSelector(
  getStoresState,
  (state: Stores) => state.loadingStoreId,
);
export const getOrderItemsStatisticsList = createSelector(
  getStoresState,
  (state: Stores) => state.orderItemsStatisticsList,
);

export const getStoresUserExperience = createSelector(
  getStoresState,
  (state: Stores) => state.storesUserExperience,
);

export const getSelectedStoreState = createSelector(
  getStoresState,
  (state: Stores) => state.selectedStore,
);

export const getSelectedStoreUserExperience = createSelector(
  getStoresState,
  (state: Stores) => {
    if (state.selectedStore.store.id < 0) {
      return initialStoreUserExperience;
    } else {
      return state.storesUserExperience[state.selectedStore.store.id];
    }
  },
);
export const getStoreValidations = createSelector(
  getStoresState,
  (state: Stores) => state.validationStatus,
);

export const getMynextLogin = createSelector(
  getStoresState,
  (state: Stores) => state.mynextLogin,
);

export const getClientStoreByAliasName = createSelector(
  getStoresState,
  (state: Stores, props) =>
    state.list.data.find(
      (clientStore) => clientStore.aliasName == props.aliasName,
    ),
);

export const getNotificationSubscriptions = createSelector(
  getStoresState,
  (state: Stores) => state.notificationSubscriptions,
);
export const getNotificationSubscriptionsByStoreId = createSelector(
  getStoresState,
  (state: Stores, props) =>
    Object.prototype.hasOwnProperty.call(
      state.notificationSubscriptions,
      props.storeId,
    ),
);

export const getHubriseLogin = createSelector(
  getStoresState,
  (state: Stores) => state.hubriseLogin,
);
export const getHubriseLogout = createSelector(
  getStoresState,
  (state: Stores) => state.hubriseLogout,
);
export const clearHubriseData = createSelector(
  getStoresState,
  (state: Stores) => state.hubriseLogout,
);

export const getCustomersState = createFeatureSelector<Customers>('customers');
export const getCustomersList = createSelector(
  getCustomersState,
  (state: Customers) => state.customersList,
);
export const getTopOrderCustomersList = createSelector(
  getCustomersState,
  (state: Customers) => state.topOrderCustomers,
);
export const getTopReservationCustomersList = createSelector(
  getCustomersState,
  (state: Customers) => state.topReservationCustomersList,
);
export const getSelectedStoreRelation = createSelector(
  getStoresState,
  (state: Stores) => state.selectedStore.store.relation,
);

export const getCustomEndpointState =
  createFeatureSelector<CustomEndpoint>('customEndpoint');
export const getCustomEndpoint = createSelector(
  getCustomEndpointState,
  (state: CustomEndpoint) => state.customEndpoint,
);

export const getEventState = createFeatureSelector<EventState>('events');

export const getCustomerDashboardState =
  createFeatureSelector<CustomerDashboardState>('dashboardCustomerDetail');
export const getCustomerDashboard = createSelector(
  getCustomerDashboardState,
  (state: CustomerDashboardState) => state,
);
export const getCustomerDetail = createSelector(
  getCustomerDashboardState,
  (state: CustomerDashboardState) => state.selecteddDbCustomer,
);
export const getSelectedCustomerId = createSelector(
  getCustomerDashboardState,
  (state: CustomerDashboardState) => state.selectedDbCustomerId,
);
export const getBasicCustomerDetail = createSelector(
  getCustomerDashboardState,
  (state: CustomerDashboardState) => state.loadedCustomer,
);
export const getCustomerOrderSummary = createSelector(
  getCustomerDashboardState,
  (state: CustomerDashboardState) => state.dBCustomerOrderSummary,
);
export const getCustomerReservationStatistics = createSelector(
  getCustomerDashboardState,
  (state: CustomerDashboardState) => state.dBCustomerReservationStatistics,
);
export const getCustomerEventBookingsStatistics = createSelector(
  getCustomerDashboardState,
  (state: CustomerDashboardState) => state.dBCustomerEventBookingsStatistics,
);
export const getCustomerLoyaltyProfile = createSelector(
  getCustomerDashboardState,
  (state: CustomerDashboardState) => state.dBCustomerLoyaltyProfile,
);
export const getCustomerOrderItems = createSelector(
  getCustomerDashboardState,
  (state: CustomerDashboardState) => state.dBCustomerOrderItems,
);
export const getCustomerRecentOrders = createSelector(
  getCustomerDashboardState,
  (state: CustomerDashboardState) => state.dBCustomerRecentOrders,
);
export const getCustomerLoyaltyTrasactions = createSelector(
  getCustomerDashboardState,
  (state: CustomerDashboardState) => state.dBCustomerLoyaltyTrasactions,
);
export const getCustomerOrderItemList = createSelector(
  getCustomerDashboardState,
  (state: CustomerDashboardState) => state.dBCutomerOrderItemList,
);
export const getCutomerReservationList = createSelector(
  getCustomerDashboardState,
  (state: CustomerDashboardState) => state.dBCutomerReservationList,
);

export const getRefunds = createSelector(
  getStoresState,
  (state: Stores) => state.refunds,
);

export const getProductsState = createSelector(
  getStoresState,
  (state: Stores) => state.products,
);

export const getProducts = createSelector(
  getProductsState,
  (state) => state.products,
);

export const getProductsStatus = createSelector(
  getProductsState,
  (state) => state.status,
);

export const getProductsError = createSelector(
  getProductsState,
  (state) => state.error,
);

export const getPaymentTransactions = createSelector(
  getStoresState,
  (state: Stores) => state.paymentTransactions,
);
